import React, { useState, useEffect, useCallback } from 'react';
import { useBooking } from '../context/BookingContext';
import { apiService } from '../services/apiService';

const BookingSidebar = ({ onContinue }) => {
  const { state, dispatch, formatCurrency, formatDisplayDate, calculateNights } = useBooking();

  const nights = calculateNights();
  const totalQtyInCart = state.cart.reduce((acc, item) => acc + item.quantity, 0);
  const requiredRooms = state.searchState?.room_guest?.length || 0;

  const isCheckoutEnabled = totalQtyInCart === requiredRooms && totalQtyInCart > 0;
  
  // NEW: Track if all required rooms are selected
  const allRoomsSelected = totalQtyInCart === requiredRooms && totalQtyInCart > 0;

  const [expandedSections, setExpandedSections] = useState({
    bookingDetails: true,
    selectedRooms: true,
    priceBreakdown: true,
    taxDetails: false
  });

  // State for available promos
  const [availablePromos, setAvailablePromos] = useState([]);
  const [selectedPromoCode, setSelectedPromoCode] = useState(null);
  const [appliedPromoCode, setAppliedPromoCode] = useState(state.searchState?.promo_code || null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState(false);
  const [currentPromoDiscountId, setCurrentPromoDiscountId] = useState(null);

  // Track cart size when promo was applied
  const [promoAppliedCartSize, setPromoAppliedCartSize] = useState(0);
  // Track if promo was auto-cleared
  const [promoAutoCleared, setPromoAutoCleared] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Monitor cart changes and clear promo when rooms are removed
  useEffect(() => {
    // If promo is applied and we have a tracked cart size
    if (state.hasDiscount && state.promoCode && promoAppliedCartSize > 0) {
      // Check if cart size has decreased (room removed)
      if (state.cart.length < promoAppliedCartSize) {
        console.log('Room removed, clearing promo code');
        setPromoAutoCleared(true);
        handleClearPromo();
        setPromoAppliedCartSize(0);
        
        // Auto-hide the notification after 5 seconds
        setTimeout(() => {
          setPromoAutoCleared(false);
        }, 5000);
      }
      // If cart size increased, update the tracked size
      else if (state.cart.length > promoAppliedCartSize) {
        setPromoAppliedCartSize(state.cart.length);
      }
    }
  }, [state.cart.length, promoAppliedCartSize, state.hasDiscount, state.promoCode]);

  // Clear promo if cart becomes empty
  useEffect(() => {
    if (state.cart.length === 0 && state.hasDiscount) {
      handleClearPromo();
      setPromoAppliedCartSize(0);
    }
  }, [state.cart.length]);

  // SYNC: Listen for SearchBar promo applications
useEffect(() => {
  // If SearchBar has a promo applied and sidebar doesn't know about it
  if (state.searchState.promo_code && !state.sidebarPromoApplied && !appliedPromoCode) {
    console.log('🔄 Syncing SearchBar promo to sidebar:', state.searchState.promo_code);
    
    // Update sidebar state to match SearchBar
    setAppliedPromoCode(state.searchState.promo_code);
    setSelectedPromoCode(state.searchState.promo_code);
    setPromoSuccess(true);
    setPromoError('');
    
    // Update context sidebar tracking
    dispatch({ 
      type: 'SET_SIDEBAR_PROMO', 
      payload: { 
        promoCode: state.searchState.promo_code, 
        applied: true 
      } 
    });
  }
}, [state.searchState.promo_code, state.sidebarPromoApplied, appliedPromoCode, dispatch]);

// SYNC: When sidebar clears promo, notify SearchBar
useEffect(() => {
  // If sidebar promo is cleared but SearchBar still shows it
  if (!state.sidebarPromoApplied && state.searchState.promo_code && !appliedPromoCode) {
    console.log('🔄 Sidebar promo cleared, but SearchBar still has promo - clearing');
    // This will trigger the SearchBar's sync effect
  }
}, [state.sidebarPromoApplied, state.searchState.promo_code, appliedPromoCode]);


// SYNC: When sidebar promo is cleared from context, reset local state
useEffect(() => {
  if (!state.sidebarPromoApplied && state.searchState.promo_code === '' && appliedPromoCode) {
    console.log('🔄 Sidebar promo flag cleared, resetting local state');
    setAppliedPromoCode(null);
    setSelectedPromoCode(null);
    setPromoSuccess(false);
    setPromoError('');
  }
}, [state.sidebarPromoApplied, state.searchState.promo_code, appliedPromoCode]);

// SYNC: When SearchBar promo_code becomes empty, clear only SearchBar-driven sidebar state
useEffect(() => {
  if (!state.searchState.promo_code && appliedPromoCode && !state.sidebarPromoApplied) {
    console.log('🔄 SearchBar promo cleared, clearing sidebar promo');
    setAppliedPromoCode(null);
    setSelectedPromoCode(null);
    setPromoSuccess(false);
    setPromoError('');
    setPromoAppliedCartSize(0);
    
    // Also clear the discount if still present
    if (state.hasDiscount) {
      dispatch({ type: 'CLEAR_DISCOUNT' });
    }
  }
}, [state.searchState.promo_code, appliedPromoCode, state.sidebarPromoApplied, state.hasDiscount, dispatch]);


  // Helper function to check if any non-stackable offers are applied
  const hasNonStackableOffersApplied = useCallback(() => {
    if (!state.cart || state.cart.length === 0) return false;
    
    return state.cart.some(item => {
      // Check for free night offers
      if (item.isFreeNightOffer) return true;
      if (item.freeNights > 0) return true;
      
      // Check for rate plan offers that are NOT stackable
      if (item.ratePlan?.offer) {
        const offer = item.ratePlan.offer;
        // If the offer has is_stackable flag, respect it
        if (offer.is_stackable === false) return true;
        // If no stackable info and it's not a promo, treat as non-stackable
        if (offer.is_promo_code !== true) return true;
      }
      
      // Check for discount info that's not stackable
      if (item.discountInfo && item.discountInfo.is_stackable === false) {
        return true;
      }
      
      // Check for offer name that's not a promo
      if (item.offerName && !item.offerName.toLowerCase().includes('promo')) {
        return true;
      }
      
      return false;
    });
  }, [state.cart]);

  // Helper function to check if a specific promo is stackable with current offers
  const canApplyPromoWithCurrentOffers = useCallback((promoCode) => {
    // Find the promo in available promos
    const promo = availablePromos.find(p => p.code === promoCode);
    
    // If promo is stackable, it can be combined with any existing offers
    if (promo?.is_stackable === 1 || promo?.is_stackable === true) {
      return true;
    }
    
    // For non-stackable promos, check if there are any other offers applied
    if (hasNonStackableOffersApplied()) {
      return false;
    }
    
    return true;
  }, [availablePromos, hasNonStackableOffersApplied]);

  // Prepare cart data for promo API
  const prepareCartDataForPromo = useCallback((responseData = null) => {
  if (!state.cart || state.cart.length === 0) return null;
  
  const formatDate = (d) => {
    const date = new Date(d);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };
  
  const totalNights = calculateNights();
  
  // Calculate total guests
  const totalGuests = state.cart.reduce((total, item) => {
    return total + (item.adultsCount || 2) + (item.childrenCount || 0);
  }, 0);
  
  const roomDetails = state.cart.map((item, index) => {
    let adultsCount = 2;
    let childrenCount = 0;
    let childAges = [];
    
    if (item.guestInfo) {
      adultsCount = item.guestInfo.adults || item.adultsCount || 2;
      childrenCount = item.guestInfo.children || item.childrenCount || 0;
      childAges = item.guestInfo.childAges || [];
    } else if (item.originalGuestConfig) {
      adultsCount = item.originalGuestConfig.adults || 2;
      childrenCount = item.originalGuestConfig.children || 0;
      childAges = item.originalGuestConfig.childAges || [];
    } else if (state.searchState?.room_guest && item.roomNumber) {
      const roomGuest = state.searchState.room_guest[item.roomNumber - 1];
      if (roomGuest) {
        adultsCount = roomGuest.no_of_adults || 2;
        childrenCount = roomGuest.no_of_children || 0;
        childAges = roomGuest.child_ages || [];
      }
    } else {
      adultsCount = item.adultsCount || 2;
      childrenCount = item.childrenCount || 0;
      childAges = item.childAges || [];
    }
    
    const pricePerNight = item.roomPrice || (item.totalForStay / totalNights);
    const originalPricePerNight = item.originalPrice || pricePerNight;
    
    const formattedChildAges = Array.isArray(childAges) && childAges.length > 0
      ? childAges.map(age => {
          if (typeof age === 'object' && age !== null) {
            return { age: parseInt(age.age, 10) };
          }
          return { age: parseInt(age, 10) };
        })
      : [];
    
      // Get discount_id - PRIORITIZE EXISTING OFFER ID WHEN STACKING
    let discountId = null;
    
    // CRITICAL: If this cart item has a stackable promo applied AND an existing offer,
    // we need to use the EXISTING OFFER'S ID, not the promo's ID
    if (item.hasExistingOffer && item.isStackableApplied && item.ratePlan?.offer?.id) {
      discountId = item.ratePlan.offer.id;
      console.log(`💰 Using EXISTING offer ID for room ${index + 1}: ${discountId}`);
    } 
    // Otherwise, try to get from API response or existing discountId
    else if (responseData?.room_details && responseData.room_details[index]) {
      discountId = responseData.room_details[index].discount_id || 
                   responseData.room_details[index].offer_id ||
                   responseData.room_details[index].offer?.id;
    }
    
    // Also check from the room's own data if available
    if (!discountId && item.discountId) {
      discountId = item.discountId;
    }
    
    return {
      room_number: index + 1,
      category_id: item.categoryId || item.roomId,
      category_name: item.roomName,
      rate_plan_id: item.ratePlanId,
      rate_plan_name: item.rateName,
      adults_count: adultsCount,
      children_count: childrenCount,
      child_ages: formattedChildAges,
      room_price: parseFloat(pricePerNight),
      original_price: parseFloat(originalPricePerNight),
      quantity: item.quantity || 1,
      offer_applied: item.offerName || '',
      ...(discountId && { discount_id: discountId })
    };
  });
  
  const subtotal = state.subtotal || state.cart.reduce((sum, item) => sum + (item.totalForStay || 0), 0);
  
  const payload = {
    promo_code: selectedPromoCode ? selectedPromoCode.toUpperCase() : null,
    booking_details: {
      hotel_id: parseInt(state.searchState.hotelId),
      hotel_name: state.searchState.hotel,
      check_in: formatDate(state.searchState.checkin),
      check_out: formatDate(state.searchState.checkout),
      total_nights: totalNights,
      total_guests: totalGuests,
      total_rooms: state.cart.reduce((total, item) => total + (item.quantity || 1), 0)
    },
    room_details: roomDetails,
    subtotal: parseFloat(subtotal)
  };
  
  // If we have a discount_id at the booking level, include it
  if (currentPromoDiscountId) {
    payload.discount_id = currentPromoDiscountId;
  }
  
  return payload;
}, [state.cart, state.searchState, state.subtotal, selectedPromoCode, calculateNights, currentPromoDiscountId]);

  // Fetch available promos from the API response
  const fetchAvailablePromos = useCallback(async () => {
    if (!state.searchState.hotelId || !state.searchState.checkin || !state.searchState.checkout) {
      return;
    }
    
    try {
      const formatDate = (d) => {
        const date = new Date(d);
        return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
      };
      
      const searchParams = {
        startDate: formatDate(state.searchState.checkin),
        endDate: formatDate(state.searchState.checkout),
        rooms: state.rooms.length,
        guests: state.searchState.guestCount,
        roomsData: state.rooms,
      };
      
      const hotelData = await apiService.getHotelData(state.searchState.hotelId, searchParams);
      
      if (hotelData.available_promo_codes && hotelData.available_promo_codes.length > 0) {
        setAvailablePromos(hotelData.available_promo_codes);
      } else {
        setAvailablePromos([]);
      }
    } catch (error) {
      console.error('Error fetching promos:', error);
      setAvailablePromos([]);
    }
  }, [state.searchState.hotelId, state.searchState.checkin, state.searchState.checkout, state.rooms.length, state.searchState.guestCount, state.rooms]);

  // Fetch promos when hotel/dates change (even when cart is empty)
  useEffect(() => {
    if (state.searchState.hotelId) {
      fetchAvailablePromos();
    }
  }, [state.searchState.hotelId, state.searchState.checkin, state.searchState.checkout, fetchAvailablePromos]);

  // Handle apply promo
const handleApplyPromo = async (promoCode) => {

   const upperPromoCode = promoCode.toUpperCase();
  
  if (!upperPromoCode) return;
  
  
  // Check if all required rooms are selected
  if (!allRoomsSelected) {
    // setPromoError(`Please add all ${requiredRooms} required room${requiredRooms !== 1 ? 's' : ''} before applying a promo code.`);
    return;
  }
  
  // Check if cart is empty
  if (state.cart.length === 0) {
    // setPromoError('Please add rooms to your cart before applying a promo code.');
    return;
  }
  
  // Check if promo can be applied with current offers
 
  if (!canApplyPromoWithCurrentOffers(upperPromoCode)) {
    const promo = availablePromos.find(p => p.code.toUpperCase() === upperPromoCode);
    if (promo?.is_stackable !== 1) {
      // setPromoError('This promo code cannot be combined with existing offers on selected rooms.');
      return;
    }
  }
  
  setApplyingPromo(true);
  setPromoError('');
  setPromoSuccess(false);


  const originalCartState = {
    cart: JSON.parse(JSON.stringify(state.cart)),
    subtotal: state.subtotal,
    taxes: state.taxes,
    total: state.total,
    finalTotal: state.finalTotal,
    hasDiscount: state.hasDiscount,
    discountAmount: state.discountAmount,
    promoCode: state.promoCode
};

localStorage.setItem('original_cart_state', JSON.stringify(originalCartState));
  
  try {
    const promoData = prepareCartDataForPromo();
     promoData.promo_code = upperPromoCode;
    if (!promoData) {
      setPromoError('Unable to prepare cart data');
      setApplyingPromo(false);
      return;
    }
    
    promoData.promo_code = promoCode;

    const selectedOfferDetails = state.cart
      .filter(item => item.ratePlan?.offer?.id && !item.ratePlan.offer.is_promo_code)
      .map(item => ({
        room_number: item.roomNumber,
        offer_id: item.ratePlan.offer.id,
        discount_id: item.ratePlan.offer.id,
        category_id: item.categoryId,
        rate_plan_id: item.ratePlanId
      }));

    if (selectedOfferDetails.length > 0) {
      promoData.offer_details = selectedOfferDetails;
    }

    const selectedPromo = availablePromos.find(p => p.code.toUpperCase() === upperPromoCode);
    promoData.is_stackable = selectedPromo?.is_stackable === 1 || selectedPromo?.is_stackable === true;

    console.log('📦 Sending promo data to API:', JSON.stringify(promoData, null, 2));
    
    const promoResponse = await apiService.applyPromoCodeToCart(promoData);
    
    console.log('📦 Cart promo validation response:', promoResponse);
    
    if (!promoResponse.success) {
      setPromoError(promoResponse.message || 'Invalid promo code');
      setApplyingPromo(false);
      return;
    }
    
   const responseData = promoResponse.data;
const discountAmount = responseData?.savings_summary?.total_savings || 0;
const discountedSubtotal = responseData?.discounted_charges?.subtotal || 0;
const discountedTaxes = responseData?.discounted_charges?.taxes?.total_tax || 0;
const discountedTotal = responseData?.discounted_charges?.total || 0;
const roomDetailsFromResponse = responseData?.room_details || [];

let discountId = null;
if (responseData?.offer_id) {
  discountId = responseData.offer_id;
} else if (responseData?.discount_id) {
  discountId = responseData.discount_id;
} else if (responseData?.id) {
  discountId = responseData.id;
} else if (responseData?.room_details && responseData.room_details[0]?.discount_id) {
  discountId = responseData.room_details[0].discount_id;
} else if (responseData?.room_details && responseData.room_details[0]?.offer_id) {
  discountId = responseData.room_details[0].offer_id;
} else if (responseData?.room_details && responseData.room_details[0]?.offer?.id) {
  discountId = responseData.room_details[0].offer.id;
}

// Store the discount_id for future use
if (discountId) {
  setCurrentPromoDiscountId(discountId);
  console.log('💰 Stored discount_id from API:', discountId);
} else {
  console.warn('⚠️ No discount_id found in API response:', responseData);
}

console.log('📦 API Response taxes:', {
  discountedTaxes,
  total_tax: responseData?.discounted_charges?.taxes?.total_tax,
  tax_breakdown: responseData?.discounted_charges?.taxes
});

if (discountAmount === 0) {
  setPromoError('Promo code could not be applied to this booking.');
  setApplyingPromo(false);
  return;
}

// Calculate tax per room from the total discounted taxes
const totalRooms = state.cart.length;
const taxPerRoom = discountedTaxes / totalRooms;

console.log(`Total tax: ${discountedTaxes}, Rooms: ${totalRooms}, Tax per room: ${taxPerRoom}`);

// CRITICAL FIX: Update cart items with correct tax data
// const updatedCart = state.cart.map((item, index) => {
//   const roomDetail = roomDetailsFromResponse.find(r => r.room_number === index + 1);
  
//   if (roomDetail) {
//     // Get the discounted price for this room
//     const discountedPricePerNight = roomDetail.discounted_price_per_night;
//     const discountedTotalForStay = discountedPricePerNight * nights;
    
//     // Calculate the correct tax for this room (should be 18% of discounted total)
//     // But use the API returned tax per room if available
//     let roomTaxAmount = taxPerRoom; // Default to distributed tax
    
//     // Try to get tax from room detail if available
//     if (roomDetail.total_tax_amount) {
//       roomTaxAmount = roomDetail.total_tax_amount;
//     } else if (roomDetail.tax_amount) {
//       roomTaxAmount = roomDetail.tax_amount;
//     } else if (responseData?.discounted_charges?.taxes?.breakdown?.[index]) {
//       roomTaxAmount = responseData.discounted_charges.taxes.breakdown[index].total_tax;
//     }
    
//     console.log(`Room ${index + 1} - Discounted total: ${discountedTotalForStay}, Tax: ${roomTaxAmount} (${(roomTaxAmount/discountedTotalForStay * 100).toFixed(2)}%)`);
    
//     return {
//       ...item,
//       roomName: item.roomName,
//       rateName: item.rateName,
//       categoryId: item.categoryId,
//       roomId: item.roomId,
//       ratePlanId: item.ratePlanId,
//       roomNumber: item.roomNumber,
//       quantity: item.quantity,
//       nights: item.nights,
//       discountedPricePerNight: discountedPricePerNight,
//       roomPrice: discountedPricePerNight,
//       originalPrice: roomDetail.original_price_per_night || item.originalPrice,
//       discountAmount: roomDetail.discount_amount || 0,
//       // CRITICAL: Save the CORRECT tax amount (68 for total stay, not per night)
//       taxData: {
//         total_tax_amount: roomTaxAmount, // This should be 68 for the entire stay
//         total_cgst_amount_after_tax: roomTaxAmount / 2,
//         total_sgst_amount_after_tax: roomTaxAmount / 2,
//         total_cess_amount: 0,
//       },
//       ratePlan: {
//         ...item.ratePlan,
//         offer: item.ratePlan?.offer ? {
//           ...item.ratePlan.offer,
//           overridden_by_promo: true,
//           is_overridden: true
//         } : null
//       },
//       offerName: item.offerName ? `${item.offerName} (overridden by promo)` : '',
//       isFreeNightOffer: false,
//       freeNights: 0,
//       hasPromoApplied: true,
//       promoCodeApplied: promoCode
//     };
//   }
  
//   return item;
// });
  const updatedCart = state.cart.map((item, index) => {
  const roomDetail = roomDetailsFromResponse.find(r => r.room_number === index + 1);
  
  if (roomDetail) {
    const discountedPricePerNight = roomDetail.discounted_price_per_night;
    const discountedTotalForStay = discountedPricePerNight * nights;
    
    let roomTaxAmount = taxPerRoom;
    
    if (roomDetail.total_tax_amount) {
      roomTaxAmount = roomDetail.total_tax_amount;
    } else if (roomDetail.tax_amount) {
      roomTaxAmount = roomDetail.tax_amount;
    } else if (responseData?.discounted_charges?.taxes?.breakdown?.[index]) {
      roomTaxAmount = responseData.discounted_charges.taxes.breakdown[index].total_tax;
    }
    
    // ⭐ CRITICAL: Check if the room has an existing offer
    const hasExistingOffer = !!(item.ratePlan?.offer && !item.ratePlan.offer.is_promo_code);
    
    // ⭐ Get stackable status from the promo
    const promo = availablePromos.find(p => p.code.toUpperCase() === upperPromoCode);
    const isStackable = promo?.is_stackable === 1;
    
    // ⭐ is_stackable_applied should be true ONLY if:
    // 1. The promo is stackable AND
    // 2. The room has an existing offer to stack with
    const isStackableApplied = isStackable && hasExistingOffer;
    
    console.log(`Room ${index + 1} - hasExistingOffer: ${hasExistingOffer}, isStackable: ${isStackable}, isStackableApplied: ${isStackableApplied}`);
    
    // ⭐ FIX: Determine which discount_id to use
    let roomDiscountId = null;
    
    if (hasExistingOffer && isStackableApplied) {
      // When stacking: Use the EXISTING OFFER'S ID from the cart item
      roomDiscountId = item.ratePlan?.offer?.id || 
                       item.discountId || 
                       item.ratePlan?.offer?.discount_id;
      console.log(`💰 Room ${index + 1} - Using EXISTING offer ID for stacking: ${roomDiscountId}`);
    } else if (roomDetail.discount_id || roomDetail.offer_id || roomDetail.offer?.id) {
      // When promo is the only offer: Use the promo's discount ID from API response
      roomDiscountId = roomDetail.discount_id || 
                       roomDetail.offer_id || 
                       roomDetail.offer?.id || 
                       discountId;
      console.log(`💰 Room ${index + 1} - Using PROMO discount ID: ${roomDiscountId}`);
    } else if (discountId) {
      roomDiscountId = discountId;
      console.log(`💰 Room ${index + 1} - Using fallback discount ID: ${roomDiscountId}`);
    }

    return {
      ...item,
      roomName: item.roomName,
      rateName: item.rateName,
      categoryId: item.categoryId,
      roomId: item.roomId,
      ratePlanId: item.ratePlanId,
      roomNumber: item.roomNumber,
      quantity: item.quantity,
      nights: item.nights,
      discountedPricePerNight: discountedPricePerNight,
      roomPrice: discountedPricePerNight,
      originalPrice: roomDetail.original_price_per_night || item.originalPrice,
      discountAmount: roomDetail.discount_amount || 0,
      discountId: roomDiscountId,  // Store the CORRECT ID based on stacking scenario
      taxData: {
        total_tax_amount: roomTaxAmount,
        total_cgst_amount_after_tax: roomTaxAmount / 2,
        total_sgst_amount_after_tax: roomTaxAmount / 2,
        total_cess_amount: 0,
      },
      ratePlan: {
        ...item.ratePlan,
        offer: item.ratePlan?.offer ? {
          ...item.ratePlan.offer,
          overridden_by_promo: !isStackableApplied,
          is_overridden: !isStackableApplied,
          is_stackable_applied: isStackableApplied,
          // Store both IDs for reference
          existing_offer_id: hasExistingOffer ? (item.ratePlan.offer.id || item.discountId) : null,
          promo_discount_id: !hasExistingOffer ? roomDiscountId : null
        } : null
      },
      offerName: isStackableApplied 
        ? item.offerName  // Keep original offer name if stacking
        : (item.offerName ? `${item.offerName} (overridden by promo)` : ''),
      isFreeNightOffer: false,
      freeNights: 0,
      hasPromoApplied: true,
      promoCodeApplied: promoCode,
      // ⭐ CRITICAL FLAGS
      isStackableApplied: isStackableApplied,
      hasExistingOffer: hasExistingOffer,
      offerOverriddenByPromo: hasExistingOffer && !isStackableApplied
    };
  }
  
  return item;
}); 
    // Update state
    setAppliedPromoCode(promoCode);
    setSelectedPromoCode(promoCode);
    setPromoSuccess(true);
    setPromoError('');
    setPromoAutoCleared(false);
    
    // Track the cart size when promo was applied
    setPromoAppliedCartSize(state.cart.length);


    // ADD THIS - Notify SearchBar about promo application
      dispatch({ 
        type: 'SET_SIDEBAR_PROMO', 
        payload: { 
          promoCode: promoCode, 
          applied: true 
        } 
      });
    
    // Apply the promo discount to cart with updated cart items and tax data
    dispatch({
      type: 'APPLY_MANUAL_PROMO',
      payload: {
        discountAmount: discountAmount,
        promoCode: promoCode,
        discountedSubtotal: discountedSubtotal,
        discountedTaxes: discountedTaxes,
        discountedTotal: discountedTotal,
        finalTotal: discountedTotal,
        isStackable: true,
        promoCodeInfo: {
          ...responseData?.promo_code_info,
          id: responseData?.room_details?.[0]?.offer?.id,
          room_details: responseData?.room_details,
          discounted_charges: responseData?.discounted_charges,
          savings_summary: responseData?.savings_summary
        },
        updatedCart: updatedCart,
        fromWidget: false
      }
    });
    
    // CRITICAL: Save to localStorage for persistence when navigating away and back
    const persistentState = {
      cart: updatedCart,
      hasDiscount: true,
      promoCode: promoCode,
      discountAmount: discountAmount,
      discountedSubtotal: discountedSubtotal,
      discountedTaxes: discountedTaxes,
      discountedTotal: discountedTotal,
      timestamp: Date.now()
    };
    
    localStorage.setItem('booking_promo_state', JSON.stringify(persistentState));
    
    console.log('✅ Promo applied successfully with tax data:', {
      discountAmount,
      discountedTaxes,
      discountedTotal,
      roomTaxData: updatedCart.map(r => r.taxData)
    });
    
    // Refresh available promos after applying
    setTimeout(() => {
      fetchAvailablePromos();
    }, 500);
    
  } catch (error) {
    console.error('Promo validation error:', error);
    setPromoError('Could not validate code. Please try again.');
  } finally {
    setApplyingPromo(false);
  }
};


// Add this useEffect to restore promo state when component mounts
useEffect(() => {
  // Restore promo state from localStorage when component mounts
  const savedPromoState = localStorage.getItem('booking_promo_state');
  if (savedPromoState) {
    const promoState = JSON.parse(savedPromoState);
    const now = Date.now();
    const oneHour = 60 * 60 * 1000; // Consider state valid for 1 hour
    
    // Check if state is still valid (not too old)
    if (now - promoState.timestamp < oneHour && promoState.cart?.length > 0) {
      console.log('🔄 Restoring promo state from localStorage');
      
      // Only restore if current cart matches saved cart structure
      if (state.cart.length === promoState.cart.length) {
        dispatch({
          type: 'RESTORE_PROMO_STATE',
          payload: promoState
        });
        
        setAppliedPromoCode(promoState.promoCode);
        setSelectedPromoCode(promoState.promoCode);
        setPromoSuccess(true);
        setPromoAppliedCartSize(promoState.cart.length);
      }
    } else {
      // Clear expired state
      localStorage.removeItem('booking_promo_state');
    }
  }
}, []); // Run once on mount

  // Handle clear promo
  // const handleClearPromo = () => {
  //   dispatch({ type: 'CLEAR_DISCOUNT' });
  //   dispatch({ type: 'UPDATE_SEARCH_STATE', payload: { promo_code: '', promo_from_widget: false } });
  //   setSelectedPromoCode(null);
  //   setAppliedPromoCode(null);
  //   setPromoSuccess(false);
  //   setPromoError('');
  //   setPromoAppliedCartSize(0);

  //     // ADD THIS - Notify SearchBar promo is cleared
  // dispatch({ type: 'CLEAR_SIDEBAR_PROMO' });
  // };

const handleClearPromo = () => {
  console.log('🗑️ Sidebar: Clearing promo and restoring original cart state');
  
   // Clear discount_id when clearing promo
  setCurrentPromoDiscountId(null);

  // Reset stackable flags on all cart items
  const resetCart = state.cart.map(item => ({
    ...item,
    isStackableApplied: false,
    offerOverriddenByPromo: false,
    hasPromoApplied: false,
    promoCodeApplied: null,
    ratePlan: item.ratePlan ? {
      ...item.ratePlan,
      offer: item.ratePlan.offer ? {
        ...item.ratePlan.offer,
        overridden_by_promo: false,
        is_overridden: false,
        is_stackable_applied: false
      } : null
    } : null,
    offerName: item.offerName?.replace(' (overridden by promo)', '') || 
               item.offerName?.replace(' (overridden by promo)', '') || 
               item.offerName,
    roomPrice: item.originalRoomPrice || item.roomPrice,
    discountedPricePerNight: null,
    discountAmount: 0,
    taxData: item.originalTaxData || item.taxData
  }));
  
  // Try to restore from saved original state
  const savedOriginalState = localStorage.getItem('original_cart_state');
  
  if (savedOriginalState) {
    try {
      const originalState = JSON.parse(savedOriginalState);
      
      dispatch({
        type: 'RESTORE_ORIGINAL_CART_STATE',
        payload: originalState
      });
      
      localStorage.removeItem('original_cart_state');
      
      setSelectedPromoCode(null);
      setAppliedPromoCode(null);
      setPromoSuccess(false);
      setPromoError('');
      setPromoAppliedCartSize(0);
      
      dispatch({ type: 'CLEAR_SIDEBAR_PROMO' });
      dispatch({ type: 'CLEAR_PROMO' });
      
      dispatch({ 
        type: 'UPDATE_SEARCH_STATE', 
        payload: { 
          promo_code: '', 
          promo_from_widget: false 
        } 
      });
      
      dispatch({ type: 'CLEAR_DISCOUNT' });
      
      localStorage.removeItem('booking_promo_state');
      
      console.log('✅ Sidebar: Original cart restored successfully');
      return;
    } catch (error) {
      console.error('Error restoring original state:', error);
    }
  }
  
  // Fallback clear
  dispatch({ type: 'UPDATE_CART', payload: resetCart });
  setSelectedPromoCode(null);
  setAppliedPromoCode(null);
  setPromoSuccess(false);
  setPromoError('');
  setPromoAppliedCartSize(0);
  
  dispatch({ type: 'CLEAR_DISCOUNT' });
  dispatch({ type: 'CLEAR_SIDEBAR_PROMO' });
  dispatch({ type: 'CLEAR_PROMO' });
  dispatch({ 
    type: 'UPDATE_SEARCH_STATE', 
    payload: { 
      promo_code: '', 
      promo_from_widget: false 
    } 
  });
  
  localStorage.removeItem('booking_promo_state');
  localStorage.removeItem('original_cart_state');
};

  // Helper function to format discount value
  const formatDiscountValue = (discount) => {
    if (discount.discount_type === 'percentage') {
      return `${discount.discount_value}% OFF`;
    } else if (discount.discount_type === 'fixed') {
      return `${formatCurrency(parseFloat(discount.discount_value))} OFF`;
    }
    return '';
  };

  // Modified isPromoDisabled function - only for application, not selection
const isPromoApplicable = (promoCode) => {
  // Check if all required rooms are selected
  if (!allRoomsSelected) return false;
  
  // If cart is empty, cannot apply
  if (state.cart.length === 0) return false;
  
  // Check if promo is already applied
  if (appliedPromoCode && appliedPromoCode.toUpperCase() === promoCode.toUpperCase()) return false;
  
  // Check if promo can be applied with current offers
  const promo = availablePromos.find(p => p.code.toUpperCase() === promoCode.toUpperCase());
  if (!promo) return false;
  
  // If promo is NOT stackable and there are other non-stackable offers, cannot apply
  if (promo.is_stackable !== 1 && hasNonStackableOffersApplied()) {
    return false;
  }
  
  return true;
};

// Check if promo can be selected (always true, but with conditions)
const isPromoSelectable = (promoCode) => {
  // Check if promo is already applied
  if (appliedPromoCode && appliedPromoCode.toUpperCase() === promoCode.toUpperCase()) return false;
  
  // Check if there's already a promo applied from SearchBar
  if (state.searchState.promo_code && !state.hasDiscount) {
    if (state.searchState.promo_code.toUpperCase() === promoCode.toUpperCase()) return false;
  }
  
  return true;
};

// Get tooltip/helper text for disabled apply button
const getPromoDisabledReason = (promoCode) => {
  if (!allRoomsSelected) {
    return ;
    // `Please add all ${requiredRooms} required room${requiredRooms !== 1 ? 's' : ''} to apply this promo code`;
  }
  
  if (state.cart.length === 0) {
    return 'Add rooms to your cart to apply promo codes';
  }
  
  
  if (appliedPromoCode && appliedPromoCode.toUpperCase() === promoCode.toUpperCase()) {
    return 'This promo code is already applied';
  }
  
  const promo = availablePromos.find(p => p.code.toUpperCase() === promoCode.toUpperCase());
  if (promo && promo.is_stackable !== 1 && hasNonStackableOffersApplied()) {
    return 'Promo Unavailable Offer already applied';
  }
  
  return '';
};


  // UPDATED: Check if a promo is disabled for selection/application
  const isPromoDisabled = (promoCode) => {
    // FIRST: Check if there's already a promo applied from SearchBar
    if (state.searchState.promo_code && !state.hasDiscount) {
      // Compare case-insensitively
      if (state.searchState.promo_code.toUpperCase() === promoCode.toUpperCase()) {
        return true;
      }
    }
    
    // Check if promo is already applied in sidebar (case-insensitive)
    if (appliedPromoCode && appliedPromoCode.toUpperCase() === promoCode.toUpperCase()) return true;
    
    // Check if all required rooms are selected
    if (!allRoomsSelected) return true;
    
    // If cart is empty, promos are disabled
    if (state.cart.length === 0) return true;
    
    // Check if promo can be applied with current offers
    const promo = availablePromos.find(p => p.code.toUpperCase() === promoCode.toUpperCase());
    if (!promo) return true;
    
    // If promo is NOT stackable and there are other non-stackable offers, disable it
    if (promo.is_stackable !== 1 && hasNonStackableOffersApplied()) {
      return true;
    }
    
    return false;
  };

  // // UPDATED: Get tooltip/helper text for disabled promo
  // const getPromoDisabledReason = (promoCode) => {
  //   // NEW: Check if all required rooms are selected
  //   if (!allRoomsSelected) {
  //     return `Please add ${requiredRooms - totalQtyInCart} more room${requiredRooms - totalQtyInCart !== 1 ? 's' : ''} to apply promo codes`;
  //   }
    
  //   if (state.cart.length === 0) {
  //     return 'Add rooms to your cart to apply promo codes';
  //   }
  //    if (appliedPromoCode && appliedPromoCode.toUpperCase() === promoCode.toUpperCase()) {
  //   return 'This promo code is already applied';
  // }
  
  //   const promo = availablePromos.find(p => p.code === promoCode);
  //   if (promo && promo.is_stackable !== 1 && hasNonStackableOffersApplied()) {
  //     return 'This promo code cannot be combined with existing offers on selected rooms';
  //   }
  //   return '';
  // };

 const calculateRoomPricingForStay = (cartItem) => {
  const nights = calculateNights();
  let totalForStay = 0;
  let totalOriginalForStay = 0;
  let taxAmount = 0;
  
  const isFreeNightOffer = cartItem.ratePlan?.offer?.discount_form === 'free_nights';
  const freeNights = cartItem.ratePlan?.offer?.no_free_nights || 0;
  const minStayNights = cartItem.ratePlan?.offer?.min_stay_nights || 1;
  
  if (isFreeNightOffer && nights >= minStayNights && freeNights > 0) {
    totalForStay = cartItem.roomPrice * nights;
    totalOriginalForStay = (cartItem.originalPrice || cartItem.roomPrice) * nights;
  } else {
    totalForStay = cartItem.roomPrice * nights;
    totalOriginalForStay = (cartItem.originalPrice || cartItem.roomPrice) * nights;
  }
  
  // CRITICAL FIX: Use taxData from cartItem if available (for promo-applied items)
  if (cartItem.taxData && cartItem.taxData.total_tax_amount) {
    taxAmount = cartItem.taxData.total_tax_amount;
  } else if (cartItem.taxData) {
    taxAmount = (cartItem.taxData.total_cgst_amount_after_tax || 0) +
               (cartItem.taxData.total_sgst_amount_after_tax || 0) +
               (cartItem.taxData.total_cess_amount || 0);
  } else {
    taxAmount = totalForStay * 0.18;
  }
  
  return {
    totalForStay,
    totalOriginalForStay,
    taxAmount,
    isFreeNightOffer,
    freeNights,
    payableNights: Math.max(nights - freeNights, 0),
    hasDiscount: cartItem.hasDiscount || (cartItem.originalPrice > cartItem.roomPrice),
    discountAmount: (cartItem.originalPrice - cartItem.roomPrice) * nights,
    roomPricePerNight: cartItem.roomPrice,
    originalPricePerNight: cartItem.originalPrice || cartItem.roomPrice
  };
};

  // Main calculation function for display totals
  const calculateDisplayTotals = () => {
    const nights = calculateNights();
    
    if (state.cart.length === 0) {
      return {
        subtotal: 0,
        originalSubtotal: 0,
        taxes: 0,
        total: 0,
        finalTotal: 0,
        cgst: 0,
        sgst: 0,
        cess: 0,
        nights: nights,
        totalDiscount: 0,
        freeNightsSavings: 0
      };
    }
    
    // ONLY apply promo discount when:
    // 1. Cart has items
    // 2. hasDiscount is true
    // 3. NOT from widget (fromWidget === false)
    // 4. has discountedSubtotal
    const hasCartPromo = state.cart.length > 0 && 
                         state.hasDiscount && 
                         !state.fromWidget && 
                         state.discountedSubtotal;
    
    if (hasCartPromo) {
      return {
        subtotal: state.discountedSubtotal,
        originalSubtotal: state.originalSubtotal || state.subtotal,
        taxes: state.discountedTaxes,
        total: state.discountedTotal,
        finalTotal: state.discountedTotal,
        cgst: state.discountedTaxes / 2,
        sgst: state.discountedTaxes / 2,
        cess: 0,
        nights: nights,
        totalDiscount: state.discountAmount,
        freeNightsSavings: 0,
        hasCartPromo: true,
        promoCode: state.promoCode
      };
    }
    
    let totalSubtotal = 0;
    let totalOriginalSubtotal = 0;
    let totalTaxes = 0;
    let totalCGST = 0;
    let totalSGST = 0;
    let totalCess = 0;
    let totalDiscount = 0;
    let freeNightsSavings = 0;

    state.cart.forEach((item) => {
      const roomPrice = item.discountedPricePerNight || item.roomPrice;
      const originalPrice = item.originalPrice || roomPrice;
      
      const roomPricing = calculateRoomPricingForStay({
        ...item,
        roomPrice: roomPrice,
        originalPrice: originalPrice
      });
      
      totalSubtotal += roomPricing.totalForStay;
      totalOriginalSubtotal += roomPricing.totalOriginalForStay;
      totalTaxes += roomPricing.taxAmount;
      
      if (item.taxData) {
        totalCGST += item.taxData.total_cgst_amount_after_tax || 0;
        totalSGST += item.taxData.total_sgst_amount_after_tax || 0;
        totalCess += item.taxData.total_cess_amount || 0;
      }
      
      if (roomPricing.hasDiscount) {
        totalDiscount += roomPricing.discountAmount;
      }
      
      if (roomPricing.isFreeNightOffer && roomPricing.freeNights > 0) {
        const savingsFromFreeNights = roomPricing.originalPricePerNight * roomPricing.freeNights;
        freeNightsSavings += savingsFromFreeNights;
      }
    });
    
    const total = totalSubtotal + totalTaxes;
    
    return {
      subtotal: totalSubtotal,
      originalSubtotal: totalOriginalSubtotal,
      taxes: totalTaxes,
      total: total,
      finalTotal: Math.round(total),
      cgst: totalCGST,
      sgst: totalSGST,
      cess: totalCess,
      nights: nights,
      totalDiscount: totalDiscount,
      freeNightsSavings: freeNightsSavings,
      hasDiscount: totalDiscount > 0,
      hasFreeNights: freeNightsSavings > 0
    };
  };

  const displayTotals = calculateDisplayTotals();

  // Get room display info - UPDATED to show discounted prices when promo is applied
  const getRoomDisplayInfo = () => {
    const roomInfo = [];
    const nights = calculateNights();
    
    const hasCartPromo = state.cart.length > 0 && 
                         state.hasDiscount && 
                         !state.fromWidget && 
                         state.discountedSubtotal;

    state.cart.forEach((item, index) => {
      let effectiveRoomPrice = item.roomPrice;
      let effectiveOriginalPrice = item.originalPrice || item.roomPrice;
      
      if (hasCartPromo && item.discountedPricePerNight) {
        effectiveRoomPrice = item.discountedPricePerNight;
        effectiveOriginalPrice = item.originalPrice || item.roomPrice;
      }
      
      const roomPricing = calculateRoomPricingForStay({
        ...item,
        roomPrice: effectiveRoomPrice,
        originalPrice: effectiveOriginalPrice
      });
      
      let adultsCount = 2;
      let childrenCount = 0;
      
      if (item.guestInfo) {
        adultsCount = item.guestInfo.adults || item.adultsCount || 2;
        childrenCount = item.guestInfo.children || item.childrenCount || 0;
      } else if (item.originalGuestConfig) {
        adultsCount = item.originalGuestConfig.adults || 2;
        childrenCount = item.originalGuestConfig.children || 0;
      } else if (state.searchState?.room_guest) {
        const roomGuest = state.searchState.room_guest[item.roomNumber - 1];
        if (roomGuest) {
          adultsCount = roomGuest.no_of_adults || 2;
          childrenCount = roomGuest.no_of_children || 0;
        } else {
          adultsCount = item.adultsCount || 2;
          childrenCount = item.childrenCount || 0;
        }
      } else {
        adultsCount = item.adultsCount || 2;
        childrenCount = item.childrenCount || 0;
      }
      
      roomInfo.push({
        roomNumber: index + 1,
        roomName: item.roomName,
        rateName: item.rateName,
        roomPricePerNight: effectiveRoomPrice,
        originalPricePerNight: effectiveOriginalPrice,
        totalForStay: roomPricing.totalForStay,
        taxForStay: roomPricing.taxAmount,
        isFreeNightOffer: roomPricing.isFreeNightOffer,
        freeNights: roomPricing.freeNights,
        payableNights: roomPricing.payableNights,
        hasDiscount: roomPricing.hasDiscount,
        discountAmount: roomPricing.discountAmount,
        adultsCount: adultsCount,
        childrenCount: childrenCount,
        taxData: item.taxData,
        ratePlan: item.ratePlan,
        guestInfo: item.guestInfo,
        hasPromoDiscount: hasCartPromo && item.discountedPricePerNight > 0
      });
    });

    return roomInfo;
  };
  
  const roomDisplayInfo = getRoomDisplayInfo();

  const getGroupedRoomInfo = () => {
    const groups = {};
    const nights = calculateNights();

    if (state.cart.length === 0) {
      return [];
    }

    const roomGuestInfo = state.searchState.room_guest || [];

    state.cart.forEach((item, cartIndex) => {
      if (!groups[item.roomName]) {
        groups[item.roomName] = {
          name: item.roomName,
          count: 0,
          adults: 0,
          children: 0,
          totalBasePrice: 0,
          totalDiscountedPrice: 0
        };
      }

      groups[item.roomName].count += item.quantity;
      
      let adultsToAdd = 0;
      let childrenToAdd = 0;
      
      if (item.guestInfo) {
        adultsToAdd = (item.guestInfo.adults || 0) * item.quantity;
        childrenToAdd = (item.guestInfo.children || 0) * item.quantity;
      } else if (item.originalGuestConfig) {
        adultsToAdd = (item.originalGuestConfig.adults || 0) * item.quantity;
        childrenToAdd = (item.originalGuestConfig.children || 0) * item.quantity;
      } else {
        const roomNumber = item.roomNumber;
        if (roomNumber && roomGuestInfo[roomNumber - 1]) {
          const guestInfo = roomGuestInfo[roomNumber - 1];
          adultsToAdd = (guestInfo.no_of_adults || 0) * item.quantity;
          childrenToAdd = (guestInfo.no_of_children || 0) * item.quantity;
        } else {
          adultsToAdd = (item.adultsCount || 0) * item.quantity;
          childrenToAdd = (item.childrenCount || 0) * item.quantity;
        }
      }
      
      groups[item.roomName].adults += adultsToAdd;
      groups[item.roomName].children += childrenToAdd;

      const roomPricing = calculateRoomPricingForStay(item);
      groups[item.roomName].totalBasePrice += roomPricing.totalOriginalForStay;
      groups[item.roomName].totalDiscountedPrice += roomPricing.totalForStay;
    });

    return Object.values(groups);
  };

  const groupedRooms = getGroupedRoomInfo();
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);

  // Check if any promo is stackable (for UI messaging)
  const hasStackablePromos = availablePromos.some(promo => promo.is_stackable === 1);
  const hasNonStackablePromos = availablePromos.some(promo => promo.is_stackable !== 1);

  return (
    <div className="lg:sticky top-24">
      <div className="bg-white shadow-lg lg:shadow-md rounded-xl lg:rounded-lg p-5 lg:p-4 border border-gray-100">
        <div className="space-y-4 lg:space-y-3">
          {/* Stay Details Section */}
          <div className="pb-4 lg:pb-3 border-b border-gray-200">
            <button
              onClick={() => toggleSection('bookingDetails')}
              className="flex items-center justify-between w-full text-left lg:cursor-default mb-2"
            >
              <h4 className="text-xs font-semibold text-text-primary uppercase tracking">Stay Details</h4>
              <svg
                className={`lg:hidden w-4 h-4 text-text-secondary transition-transform duration-200 ${expandedSections.bookingDetails ? 'rotate-180' : ''
                  }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <div
              className={`
                lg:block overflow-hidden transition-all duration-300 ease-in-out
                ${expandedSections.bookingDetails ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 lg:max-h-none lg:opacity-100'}
              `}
            >
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase font-bold text-text-secondary tracking-tight">Check-in</span>
                    <span className="text-sm font-bold text-text-primary">
                      {formatDisplayDate(state.searchState.checkin)}
                    </span>
                    <span className="text-[11px] text-text-secondary">
                      {new Date(state.searchState.checkin).toLocaleDateString('en-US', { weekday: 'long' })} • 02:00 PM
                    </span>
                  </div>

                  <div className="self-center px-2 py-1 bg-gray-50 border border-gray-100 rounded text-[11px] font-bold text-brand-primary">
                    {nights} {nights === 1 ? 'Night' : 'Nights'}
                  </div>

                  <div className="flex flex-col text-right">
                    <span className="text-[10px] uppercase font-bold text-text-secondary tracking-tight">Check-out</span>
                    <span className="text-sm font-bold text-text-primary">
                      {formatDisplayDate(state.searchState.checkout)}
                    </span>
                    <span className="text-[11px] text-text-secondary">
                      {new Date(state.searchState.checkout).toLocaleDateString('en-US', { weekday: 'long' })} • 11:00 AM
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Selected Rooms Section */}
          <div className="pb-3 border-b border-gray-100">
            <div className="flex justify-between items-end mb-2">
              <h4 className="text-xs font-bold text-text-primary uppercase tracking">
                Guest & Rooms
              </h4>
              {state.cart.length > 0 && (
                <div className="text-right leading-none">
                  <span className="text-sm font-semibold text-text-primary">
                    {totalQtyInCart} / {requiredRooms} Rooms
                  </span>
                  <span className="mx-1 text-gray-300">•</span>
                  <span className="text-sm font-semibold text-text-primary">
                    {groupedRooms.reduce((acc, g) => acc + g.adults, 0)} Adults, {groupedRooms.reduce((acc, g) => acc + g.children, 0)} Children
                  </span>
                </div>
              )}
            </div>

            <div className={`lg:block overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.selectedRooms ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
              <div className="space-y-1">
                {groupedRooms.map((group, idx) => (
                  <div key={idx} className="flex justify-between items-center py-1 group">
                    <h5 className="font-medium text-sm text-text-primary truncate pr-4">
                      {group.count} × {group.name}
                    </h5>
                    <div className="text-[13px] font-medium text-text-secondary whitespace-nowrap flex-shrink-0">
                      {group.adults} Adults, {group.children} Children
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Price Summary Section */}
          <div className="border-gray-100">
            <div className="space-y-2">
              <h4 className="text-xs font-bold text-text-primary uppercase tracking">
                Price
              </h4>

              {/* Free Nights Offer Badge */}
              {displayTotals.hasFreeNights && (
                <div className="mb-2 p-2 bg-green-50 border border-green-100 rounded-md">
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-green-600">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-xs font-bold text-green-700">
                        FREE NIGHT OFFER APPLIED!
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {/* Promo Code Applied Section (when discount is active) */}
{(state.cart.length > 0 && state.hasDiscount && !state.fromWidget && state.promoCode && state.discountAmount > 0) && (
  <div className="pb-3 border-b border-gray-100">
    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-bold text-green-700 text-sm">
              Promo Code Applied
            </span>
          </div>
          <p className="text-xs text-green-600 mt-1">
            Code: <span className="font-mono font-bold">{state.promoCode}</span>
          </p>
          <p className="text-xs text-green-600">
            You saved {formatCurrency(state.discountAmount)}
          </p>
        </div>
        <button
          onClick={handleClearPromo}
          className="text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1 rounded hover:bg-red-50"
        >
          Remove
        </button>
      </div>
    </div>
  </div>
)}

              {/* Promo Code Applied Section */}
             {state.searchState.promo_code && !state.hasDiscount && state.cart.length > 0 && (
                <div className="pb-3 border-b border-gray-100">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          <span className="font-bold text-blue-700 text-sm">
                            Promo Code Ready
                          </span>
                        </div>
                        <p className="text-xs text-blue-600 mt-1">
                          Code: <span className="font-mono font-bold">{state.searchState.promo_code}</span>
                        </p>
                        <p className="text-xs text-blue-600">
                          Will be applied to your booking
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          // Clear the widget promo
                          dispatch({ 
                            type: 'UPDATE_SEARCH_STATE', 
                            payload: { promo_code: '', promo_from_widget: false } 
                          });
                        }}
                        className="text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1 rounded hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Promo Auto-Cleared Notification */}
              {promoAutoCleared && !state.hasDiscount && (
                <div className="pb-3 border-b border-gray-100">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <p className="text-xs text-amber-700">
                        Promo code removed due to room selection change. Please re-apply if needed.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Tariff Row */}
              <div className="flex justify-between text-sm">
                <h5 className="text-text-primary font-medium">
                  Tariff for {nights} night{nights !== 1 ? 's' : ''}
                </h5>
                <span className="text-text-primary">
                  {formatCurrency(displayTotals.subtotal)}
                </span>
              </div>

              {/* Taxes Row */}
              <div className="flex justify-between text-sm">
                <h5 className="text-text-primary font-medium">Taxes & Fees</h5>
                <span className="text-text-primary">
                  {formatCurrency(displayTotals.taxes)}
                </span>
              </div>

              {/* Total Payable Row */}
              <div className="flex justify-between items-start pt-3 mt-1 border-t border-gray-100">
                <span className="text-sm font-bold text-text-primary uppercase tracking-tight mt-1">
                  Total Payable
                </span>
                <div className="text-right">
                  <span className="text-xl font-bold text-text-primary block leading-none">
                    {formatCurrency(displayTotals.finalTotal)}
                  </span>
                  
                  {state.cart.length > 0 && (
                    <button
                      onClick={() => setIsBreakdownOpen(true)}
                      className="text-[11px] font-bold text-brand-primary underline mt-1 hover:text-brand-primary-hover transition-colors"
                    >
                      View Price Breakup
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* AVAILABLE PROMOS SECTION - Always show promos (even when cart empty) */}
          {availablePromos.length > 0 && (
            <div className="border-t border-gray-200 pt-4 mt-2">
               <div className="flex justify-between items-center mb-3">
                <h4 className="text-xs font-bold text-text-primary uppercase tracking">
                  Available Promo Codes {state.cart.length === 0 && ("Select Rooms To Apply" )} 
                </h4>
                <div className="flex items-center gap-2">
                  {state.searchState.promo_code && !state.hasDiscount && (
                    <span className="text-[10px] text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                      Promo Ready
                    </span>
                  )}
                  {!allRoomsSelected && state.cart.length > 0 && (
                    <span className="text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                      Add {requiredRooms - totalQtyInCart} more room{requiredRooms - totalQtyInCart !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
              
              {/* NEW: Room selection required message */}
              {!allRoomsSelected && state.cart.length > 0 && (
                <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg hidden">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-xs text-amber-700">
                      Please add all {requiredRooms} required room{requiredRooms !== 1 ? 's' : ''} to apply promo codes.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Stackable Info Message */}
              {hasStackablePromos && hasNonStackablePromos && allRoomsSelected && (
                <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-[11px] text-blue-700 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Some promo codes can be combined with existing offers, others cannot.
                  </p>
                </div>
              )}

               {/* Add this message when promo is already waiting */}
    {state.searchState.promo_code && !state.hasDiscount && (
      <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xs text-blue-700">
            Promo code <span className="font-mono font-bold">{state.searchState.promo_code}</span> is ready. 
            Add rooms to your cart to apply it.
          </p>
        </div>
      </div>
    )}
              
              <div className="space-y-2">
                {availablePromos.map((promo) => {
  const promoCodeUpper = promo.code.toUpperCase();
  const canApply = isPromoApplicable(promoCodeUpper);
  const canSelect = isPromoSelectable(promoCodeUpper);
  const isSelected = selectedPromoCode === promoCodeUpper;
  const isApplied = appliedPromoCode && appliedPromoCode.toUpperCase() === promoCodeUpper;
  const disabledReason = getPromoDisabledReason(promoCodeUpper);
  const isStackable = promo.is_stackable === 1;
  
  const primaryOffer = promo.category_offers?.[0];
  const discountText = primaryOffer 
    ? primaryOffer.discount_type === 'percentage' 
      ? `${primaryOffer.discount_value}%`
      : `${formatCurrency(parseFloat(primaryOffer.discount_value))}`
    : 'Special Offer';
  
  const hasMultipleOffers = promo.category_offers && promo.category_offers.length > 1;
  
  return (
    <div
      key={promo.code}
      className={`border rounded-lg p-3 transition-all ${
        isApplied
          ? 'border-green-400 bg-green-50'
          : isSelected
          ? 'border-brand-accent bg-brand-accent/5'
          : 'border-gray-200 hover:border-brand-accent/50'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Radio Button / Checkmark */}
        <div className="flex-shrink-0 pt-1">
          {isApplied ? (
            <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          ) : (
            <input
              type="radio"
              name="promoCode"
              value={promoCodeUpper}
              checked={isSelected}
              onChange={() => {
                if (canSelect && !state.searchState.promo_code) {
                  setSelectedPromoCode(promoCodeUpper);
                }
              }}
              disabled={!canSelect || !!state.searchState.promo_code}
              className="w-4 h-4 text-brand-accent focus:ring-brand-accent border-gray-300"
            />
          )}
        </div>
        
        {/* Promo Details */}
        <div className="flex-1">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex-1">
              {/* Promo Code Badge - UPPERCASE */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono font-bold text-sm bg-gray-100 px-2 py-0.5 rounded text-text-primary">
                  {promoCodeUpper}
                </span>
                <span className="font-bold text-sm text-brand-accent hidden">
                  {discountText}
                </span>
                {/* Stackable Badge */}
                {isStackable && (
                  <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-semibold hidden">
                    Stackable
                  </span>
                )}
              </div>
              
              {/* Description */}
              {promo.description ? (
                <p className="text-xs text-text-secondary mt-1.5 leading-relaxed">
                  {promo.description}
                </p>
              ) : (
                <p className="text-xs text-text-secondary mt-1.5 leading-relaxed">
                  Save {discountText.toLowerCase()} per night
                </p>
              )}
              
              {/* Multiple Offers Indicator */}
              {hasMultipleOffers && (
                <div className="flex items-center gap-1 mt-1 hidden">
                  <svg className="w-3 h-3 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-[10px] text-text-tertiary">
                    Applicable on {promo.category_offers.length} room type{promo.category_offers.length > 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
            
            {/* Apply/Select Button - Only this button gets disabled */}
            {!isApplied && (
              <button
  onClick={() => canApply && !state.searchState.promo_code && handleApplyPromo(promoCodeUpper)}
  disabled={!canApply || applyingPromo || !!state.searchState.promo_code}
  className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
    isSelected
      ? 'bg-brand-accent text-white hover:bg-brand-accent-hover'
      : 'bg-gray-100 text-text-secondary hover:bg-gray-200'
  } ${(!canApply || applyingPromo || !!state.searchState.promo_code) ? 'opacity-50 cursor-not-allowed' : ''}`}
>
  {applyingPromo && selectedPromoCode === promoCodeUpper ? (
    <div className="flex items-center gap-1">
      <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Applying...
    </div>
  ) : isSelected ? (
    !canApply ? 'Select Room' : 'Apply Now'
  ) : 'Select'}
              </button>
            )}
          </div>
          
          {/* Disabled reason tooltip - shows only for apply button, not entire card */}
          {!canApply && !isApplied && disabledReason && isSelected && (
            <p className="text-[10px] text-amber-600 mt-1">
              {disabledReason}
            </p>
          )}
        </div>
      </div>
    </div>
  );
                })}

              </div>
              
              {/* Error Message */}
              {promoError && (
                <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {promoError}
                  </p>
                </div>
              )}
              
              
            </div>
          )}

          {/* Checkout Section */}
          <div className="mt-6">
            {state.cart.length > 0 && totalQtyInCart !== requiredRooms && (
              <div className="mb-3 text-center">
                <p className="text-[12px] font-bold text-red-600 uppercase tracking-wide">
                  {totalQtyInCart < requiredRooms
                    ? `Please Add ${requiredRooms - totalQtyInCart} More Room${requiredRooms - totalQtyInCart > 1 ? 's' : ' to continue'}`
                    : `Limit Exceeded: Remove ${totalQtyInCart - requiredRooms} Room${totalQtyInCart - requiredRooms > 1 ? 's' : ''}`
                  }
                </p>
              </div>
            )}

            <button
              onClick={onContinue}
              disabled={!isCheckoutEnabled}
              className={`w-full bg-brand-accent text-text-dark-on-accent px-6 py-3.5 rounded-lg font-semibold text-base hover:bg-brand-accent-hover transition-all duration-200 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-accent shadow-md ${!isCheckoutEnabled ? 'opacity-50 cursor-not-allowed grayscale-[0.5]' : ''
                }`}
            >
              Continue to Checkout
            </button>
          </div>

          {/* Breakdown Modal */}
          {isBreakdownOpen && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
              <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                  <h3 className="font-bold text-base text-text-primary">Price Breakup Detail</h3>
                  <button
                    onClick={() => setIsBreakdownOpen(false)}
                    className="p-1.5 hover:bg-gray-200 rounded-full transition-colors text-text-secondary"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Content */}
                <div className="p-5 max-h-[60vh] overflow-y-auto">
                  <div className="space-y-4">
                    {/* Room-wise List */}
                    <div className="space-y-3">
                      <h4 className="text-[10px] font-bold text-text-secondary uppercase tracking-[0.15em] mb-2">
                        Selected Accommodations
                      </h4>
                      {roomDisplayInfo.map((room, index) => (
                        <div key={index} className="py-3 border-b border-gray-100 last:border-0">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold bg-gray-100 text-text-secondary px-1.5 py-0.5 rounded">
                                  Room {room.roomNumber}
                                </span>
                                <h5 className="font-bold text-sm text-text-primary">{room.roomName}</h5>
                              </div>
                              <p className="text-[11px] text-brand-primary font-semibold mt-1 pl-8">
                                {room.rateName}
                              </p>
                              <p className="text-[11px] text-text-secondary pl-8">
                                {room.adultsCount} Adults, {room.childrenCount} Children
                              </p>
                              {/* Show promo discount badge if applicable */}
                              {room.hasPromoDiscount && (
                                <div className="mt-1 pl-8">
                                  <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold">
                                    Promo Discount Applied
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              {room.hasPromoDiscount && room.originalPricePerNight > room.roomPricePerNight && (
                                <div className="text-[10px] text-text-secondary line-through mb-0.5">
                                  {formatCurrency(room.originalPricePerNight * nights)}
                                </div>
                              )}
                              <span className="font-bold text-sm text-text-primary">
                                {formatCurrency(room.totalForStay)}
                              </span>
                             {room.taxForStay > 0 && (
                                  <p className="text-[10px] text-text-secondary mt-0.5">
                                    Tax: {formatCurrency(room.taxForStay)}
                                  </p>
                                )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Financial Summary */}
                    <div className="mt-6 pt-4 border-t border-gray-200 space-y-3">
                      <div className="space-y-2">
                        {/* Taxes */}
                        <div className="flex justify-between text-sm text-text-secondary">
                          <span>Taxes & Fees:</span>
                          <span className="font-medium text-text-primary">
                            {formatCurrency(displayTotals.taxes)}
                          </span>
                        </div>
                      </div>

                      <div className="flex justify-between items-center pt-3 mt-1 border-t-2 border-gray-100">
                        <span className="text-base font-bold text-text-primary">Grand Total</span>
                        <span className="text-xl font-bold text-text-primary">
                          {formatCurrency(displayTotals.finalTotal)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 pt-2">
                  <button
                    onClick={() => setIsBreakdownOpen(false)}
                    className="w-full py-3 bg-text-primary text-white rounded-xl font-bold shadow-md active:scale-[0.98] transition-all"
                  >
                    Close Detail View
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingSidebar;
